/**
 * sl-ufq6.7.2: Sidebar AI credit usage indicator.
 *
 * Owner/Admin: Workspace-level credit usage (% bar).
 * All users: Personal budget limit info (if configured).
 */
import { useBackendAvailable } from '@affine/core/components/hooks/use-backend-availability';
import { useQuery } from '@affine/core/components/hooks/use-query';
import { StorageUsageBar } from '@affine/core/components/storage-usage-bar';
import { AuthService, workspaceApiFetch } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isBackendUnavailableError,
  markBackendAvailable,
  markBackendUnavailable,
} from '@affine/core/utils/backend-availability';
import { creditBalanceQuery } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'react';

import * as styles from './index.css';

// ─── Personal Budget Hook ─────────────────────────────────────────────────

interface PersonalBudget {
  allowed: boolean;
  reason: string | null;
  resetAt: string | null;
  limitMicro: string | null;
  spentMicro: string;
  behavior: string;
}

function usePersonalBudget(
  workspaceId: string,
  userId: string | null,
  backendAvailable: boolean
) {
  const [budget, setBudget] = useState<PersonalBudget | null>(null);

  useEffect(() => {
    if (!userId || !backendAvailable) return;
    let cancelled = false;

    workspaceApiFetch(
      `/api/workspace/${workspaceId}/ai/budget-limits/spend/user/${userId}`,
      { credentials: 'include' }
    )
      .then(res => {
        if (res.ok) {
          markBackendAvailable();
          return res.json();
        }
        if (res.status === 502 || res.status === 503 || res.status === 504) {
          markBackendUnavailable({
            status: res.status,
            message: res.statusText,
          });
        }
        return null;
      })
      .then(data => {
        if (!cancelled && data) setBudget(data);
      })
      .catch(error => {
        markBackendUnavailable(error);
      });

    return () => {
      cancelled = true;
    };
  }, [backendAvailable, workspaceId, userId]);

  return budget;
}

// ─── Component ────────────────────────────────────────────────────────────

export const AIUsage = () => {
  const workspace = useService(WorkspaceService).workspace;

  // Local workspaces have no backend credit ledger; skip before any hook
  // that would fire a workspace-scoped GraphQL query (WorkspacePermissionGuard
  // throws Forbidden on non-members, crashing the sidebar via ErrorBoundary).
  if (workspace.flavour === 'local') {
    return null;
  }

  return <AIUsageCloud />;
};

const AIUsageCloud = () => {
  const workspace = useService(WorkspaceService).workspace;
  const workspaceId = workspace.id;
  const backendAvailable = useBackendAvailable();

  const session = useService(AuthService).session;
  const account = useLiveData(session.account$);
  const userId = account?.id ?? null;

  const permissionService = useService(WorkspacePermissionService);
  const isOwnerOrAdmin = useLiveData(
    permissionService.permission.isOwnerOrAdmin$
  );

  const personalBudget = usePersonalBudget(
    workspaceId,
    userId,
    backendAvailable
  );
  // Only treat as active limit if limitMicro is set AND > 0
  const budgetLimitValue = personalBudget?.limitMicro
    ? Number(BigInt(personalBudget.limitMicro))
    : 0;
  const hasPersonalLimit = budgetLimitValue > 0;

  // Show if Owner/Admin OR if user has a personal budget limit
  if (!backendAvailable || (!isOwnerOrAdmin && !hasPersonalLimit)) {
    return null;
  }

  return (
    <AIUsageInner
      workspaceId={workspaceId}
      isOwnerOrAdmin={!!isOwnerOrAdmin}
      personalBudget={hasPersonalLimit ? personalBudget : null}
    />
  );
};

const AIUsageInner = ({
  workspaceId,
  isOwnerOrAdmin,
  personalBudget,
}: {
  workspaceId: string;
  isOwnerOrAdmin: boolean;
  personalBudget: PersonalBudget | null;
}) => {
  const t = useI18n();

  const { data: balanceData, isLoading: balanceLoading } = useQuery(
    {
      query: creditBalanceQuery,
      variables: { workspaceId },
    },
    {
      suspense: false,
      shouldRetryOnError: error => !isBackendUnavailableError(error),
    }
  );
  const balance = balanceData?.creditBalance ?? null;

  const workspaceDialogService = useService(WorkspaceDialogService);

  const goToAISettings = useCallback(() => {
    workspaceDialogService.open('setting', {
      activeTab: 'workspace:ai',
    });
  }, [workspaceDialogService]);

  // Personal budget info
  const budgetSpent = personalBudget?.spentMicro
    ? Number(BigInt(personalBudget.spentMicro)) / 10000
    : 0;
  const budgetLimit = personalBudget?.limitMicro
    ? Number(BigInt(personalBudget.limitMicro)) / 10000
    : 0;
  const budgetPercent =
    budgetLimit > 0 ? Math.min(100, (budgetSpent / budgetLimit) * 100) : 0;
  const budgetResetAt = personalBudget?.resetAt
    ? new Date(personalBudget.resetAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;
  const budgetColor =
    budgetPercent > 80
      ? cssVarV2('status/error')
      : cssVarV2('status/processing');

  if (!isOwnerOrAdmin && !personalBudget) {
    return null;
  }

  // Owner/Admin: show workspace credits — but NOT when a personal limit is active.
  // Personal limit takes precedence: if you have one, only your limit is shown.
  const showWorkspaceBar =
    !personalBudget &&
    isOwnerOrAdmin &&
    !balanceLoading &&
    balance &&
    balance.displayMonthlyGrant > 0;
  const monthlyGrant = balance?.displayMonthlyGrant ?? 0;
  const monthlyUsed = balance?.displayMonthlyUsed ?? 0;
  const wsPercent =
    monthlyGrant > 0 && monthlyUsed > 0
      ? Math.min(
          100,
          Math.max(0.5, Number(((monthlyUsed / monthlyGrant) * 100).toFixed(4)))
        )
      : 0;
  const wsColor = balance?.lowBalance
    ? cssVarV2('status/error')
    : cssVarV2('status/processing');

  if (showWorkspaceBar) {
    return (
      <StorageUsageBar
        className={clsx(styles.cloudUsageBlock, styles.clickableUsageBlock)}
        color={wsColor}
        fillTestId="workspace-credit-bar-inner"
        label={t['com.streamlain.user-info.usage.ai-workspace']()}
        labelColor={cssVarV2('text/primary')}
        limitText=""
        onAction={goToAISettings}
        percent={wsPercent}
        trackHeight={10}
        usedText=""
        valueTestId="workspace-credit-percent"
        valueText={`${Math.round(wsPercent)}%`}
      />
    );
  }

  if (personalBudget) {
    return (
      <StorageUsageBar
        className={clsx(styles.cloudUsageBlock, styles.clickableUsageBlock)}
        color={budgetColor}
        description={budgetResetAt ? `Reset: ${budgetResetAt}` : null}
        label={t['com.streamlain.user-info.usage.ai-budget-limit']()}
        labelColor={cssVarV2('text/primary')}
        limitText=""
        onAction={goToAISettings}
        percent={budgetPercent}
        trackHeight={10}
        usedText=""
        valueText={`${Math.round(budgetSpent)}/${Math.round(budgetLimit)}`}
      />
    );
  }

  return null;
};
